import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { db } from '../services/ApiManagement/Index';
import { isAnimal } from '../services/ApiManagement/functions/IsAnimal';
import { isRegister } from '../services/ApiManagement/functions/IsRegister';
import api from '../services/api';
import { ManterAtualizado } from '../services/ApiManagement/functions/ManterAtualizado';
import { CheckAPIAndDeleteAnimalController } from '../services/ApiManagement/DexieDB/modules/animals/useCases/CheckAPIAndDeleteAnimal/CheckAPIAndDeleteAnimalController';
import { useOnlineStatus } from './onlineStatus';

interface SyncContextData {
  syncLocal(): void;
  syncRemote(): void;
  status: 'syncing' | 'synced' | 'error' | 'waiting';
}

export const SyncContext = React.createContext<SyncContextData>(
  {} as SyncContextData,
);

export const SyncProvider: React.FC = ({ children }) => {
  const [status, setStatus] = useState<
    'syncing' | 'synced' | 'error' | 'waiting'
  >('synced');

  const isOnline = useOnlineStatus();

  /* useEffect(() => {
    if (isOnline) {
      syncLocal();
      syncRemote();
    }
  }, [isOnline]); */

  const syncLocal = useCallback(() => {
    console.log('syncLocal Running');
    setStatus('syncing');

    api
      .get('/propriedades/minhas')
      .then(response => {
        const { minhasPropriedades, colaboracoes } = response.data;

        const allProperties = minhasPropriedades.concat(colaboracoes);

        if (allProperties.length === 0) {
          setStatus('synced');
        }

        allProperties?.map(async (property: any) => {
          if (property) {
            // Salva os dados completos das propriedades
            const propertyResponse = await api.get('/propriedades', {
              headers: {
                propriedade_id: property.id,
              },
            });
            await ManterAtualizado('propriedades', propertyResponse.data);

            // Salva outros dados da propriedade
            try {
              // Animais
              const animalResponse = await api.get('/animais/listar/todos', {
                headers: {
                  propriedade_id: property.id,
                },
              });
              await CheckAPIAndDeleteAnimalController({
                animalsApi: animalResponse.data,
                propriedade_id: property.id,
                table: db,
              });

              animalResponse.data?.map(async (animal: any) => {
                await ManterAtualizado('animais', animal);
              });

              // Filtros válidos
              const filtersResponse = await api.get(
                '/animais/filtros/validos/',
                {
                  headers: {
                    propriedade_id: property.id,
                  },
                },
              );

              await ManterAtualizado('filtrosValidos', {
                ...filtersResponse.data,
                id: property.id,
              });

              // Registros
              const registerResponse = await api.get('/registros/propriedade', {
                headers: {
                  propriedade_id: property.id,
                },
              });

              registerResponse.data?.map(async (register: any) => {
                await ManterAtualizado('registros', register);
              });

              // Proprietário
              const userResponse = await api.get(
                `/usuarios/perfil/${property.proprietario_id}`,
              );

              await ManterAtualizado('usuarios', userResponse.data);

              setStatus('synced');
            } catch (err) {
              console.log(err);
            }
          }
        });
      })
      .catch(err => console.log(err));
  }, []);

  const syncRemote = useCallback(async () => {
    console.log('syncRemote Running');

    const apiObj = {
      DELETE: api.delete,
      POST: api.post,
      PUT: api.put,
    };

    const toPerform = await db.toPerform.toArray();

    toPerform.sort((a, b) => {
      return a.created_at.getTime() - b.created_at.getTime();
    });

    if (toPerform.length > 0 && isOnline) {
      setStatus('syncing');
    } else if (toPerform.length > 0 && !isOnline) {
      setStatus('waiting');
    } else if (toPerform.length === 0) {
      setStatus('synced');
    }

    return new Promise(reject => {
      toPerform.forEach(async item => {
        const threeArgs: [string, any, AxiosRequestConfig?] = [
          item.url,
          item.data,
          item.config,
        ];
        const twoArgs: [string, any] = [item.url, item.config];

        const response = await apiObj[item.method](
          ...(item.method === 'DELETE' ? twoArgs : threeArgs),
        )
          .then(async response => {
            await db.toPerform.delete(item.id);
            setStatus('synced');
            return response;
          })
          .catch(err => {
            if (axios.isAxiosError(err)) {
              console.log(err.response);
              setStatus('error');
            }
            reject(err);
          });

        if (isAnimal(item.data) && item.method == 'POST' && response) {
          const toPerformData = await db.toPerform.toArray();

          console.log(toPerformData);

          const registros = toPerformData.filter(
            reg => isRegister(reg.data) && reg.data.animal_id === item.id,
          );

          registros.forEach(async registro => {
            await api
              .post(
                `/registros/${response.data.id}`,
                { ...registro.data, animal_id: response.data.id },
                registro.config,
              )
              .then(async response => {
                await db.toPerform.delete(registro.id);
                setStatus('synced');
                return response;
              })
              .catch(err => {
                if (axios.isAxiosError(err)) {
                  console.log(err.response);
                  //setStatus('error');
                }
                reject(err);
              });
          });
        }
      });
    });
  }, []);

  return (
    <SyncContext.Provider value={{ syncLocal, syncRemote, status }}>
      {children}
    </SyncContext.Provider>
  );
};

export const useSync = () => {
  const context = useContext(SyncContext);

  return context;
};
